import { useEffect, useState } from 'react'
import { usePurchaseLoaders, useSupplierLoaders, PurchaseOrder, Supplier } from '../../../purchases'
import { useSystemStateHooks } from '../../../system'
import { useManagerState } from '../../../manager'
import { Branch } from '../../../manager'
import { DeliveryStatus } from '../../../deliveries'
import { daysAgoStr, toLocalDateStr } from '../../utils/dashboardUtils'

export type PurchasesData = {
    pendingPurchases: PurchaseOrder[]
    overduePurchases: PurchaseOrder[]
    suppliers: Supplier[]
    branches: Branch[]
}

// enabled: false when no visible card needs this data (it isn't loaded)
export const usePurchasesData = (enabled = true) => {
    const [loading, setLoading] = useState(enabled)
    const [error, setError] = useState(false)
    const [data, setData] = useState<PurchasesData | null>(null)

    const { companyId } = useSystemStateHooks()
    const { branches } = useManagerState()
    const purchaseLoaders = usePurchaseLoaders()
    const supplierLoaders = useSupplierLoaders()

    useEffect(() => {
        if (!enabled) return
        setLoading(true)
        const load = async () => {
            try {
                const id = companyId

                const [allPurchases, allSuppliers] = await Promise.all([
                    purchaseLoaders(id).loadAllPurchases(),
                    supplierLoaders(id).loadAllSuppliers()
                ])

                const activePurchases = allPurchases.filter(p =>
                    !p.archived &&
                    (p.deliveryStatus === DeliveryStatus.PENDING || p.deliveryStatus === DeliveryStatus.PARTIALLY_DELIVERED)
                )

                const sevenDaysAgo = daysAgoStr(7)
                const overduePurchases = activePurchases.filter(p =>
                    toLocalDateStr(p.creationDate) <= sevenDaysAgo
                )

                setData({
                    pendingPurchases: activePurchases,
                    overduePurchases,
                    suppliers: (allSuppliers as Supplier[]).filter(s => !s.archived),
                    branches: branches as Branch[]
                })
            } catch {
                setError(true)
            } finally {
                setLoading(false)
            }
        }
        load()
    }, [enabled])

    return { loading, error, data }
}
